import { router } from "@inertiajs/react"
import { ChevronRight, ShieldCheck } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { crewMembershipPath } from "@/routes"
import type { AtRiskRow } from "@/types/crew-analytics"

/**
 * OKS-65 — Top-5 at-risk crew members.
 *
 * Predicate is FIXED-21-day (NOT window-driven). Order is "longest
 * quiet first" per UX Spec §9 OQ-4 — the table's own server-side
 * sort. Click anywhere on a row → `crewMembershipPath(id)`.
 *
 * Empty array → positive empty card ("Nobody is at risk right now —
 * your whole crew is active.") with `CheckCircle2` glyph.
 */
interface CrewAtRiskTableProps {
  rows: AtRiskRow[]
}

const DAYS_CAP_DISPLAY = 1000

export function CrewAtRiskTable({ rows }: CrewAtRiskTableProps) {
  const { t } = useTranslation()

  if (rows.length === 0) {
    return (
      <Card data-testid="crew-analytics-at-risk-empty">
        <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
          <ShieldCheck
            className="text-muted-foreground h-8 w-8"
            aria-hidden="true"
          />
          <p className="text-muted-foreground text-sm">
            {t("analytics.crew.at_risk.empty")}
          </p>
        </CardContent>
      </Card>
    )
  }

  const navigateToMember = (id: number) => {
    router.visit(crewMembershipPath(id))
  }

  return (
    <Card data-testid="crew-at-risk-table">
      <CardHeader>
        <CardTitle>{t("analytics.crew.at_risk.title")}</CardTitle>
        <p className="text-muted-foreground text-sm">
          {t("analytics.crew.at_risk.subtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t("analytics.crew.at_risk.columns.creator")}
              </TableHead>
              <TableHead className="text-right">
                {t("analytics.crew.at_risk.columns.days_since")}
              </TableHead>
              <TableHead className="text-right">
                {t("analytics.crew.at_risk.columns.lifetime_collabs")}
              </TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.crew_membership_id}
                className="hover:bg-muted/50 cursor-pointer"
                onClick={() => navigateToMember(row.crew_membership_id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    navigateToMember(row.crew_membership_id)
                  }
                }}
                tabIndex={0}
                role="link"
                data-testid={`crew-at-risk-row-${row.crew_membership_id}`}
              >
                <TableCell className="font-medium">
                  {row.tiktok_username ??
                    t("analytics.crew.at_risk.handle_missing")}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.days_since_last_submission >= DAYS_CAP_DISPLAY
                    ? t("analytics.crew.at_risk.days_capped")
                    : t("analytics.crew.at_risk.days_value", {
                        count: row.days_since_last_submission,
                      })}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {t("analytics.crew.at_risk.collabs_value", {
                    count: row.lifetime_collabs,
                  })}
                </TableCell>
                <TableCell>
                  <ChevronRight
                    className="text-muted-foreground h-4 w-4"
                    aria-hidden="true"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
